import { useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { RouteAccessGate } from '@/components/routes/RouteAccessGate';
import { useFeatures } from '@/core/features/useFeatures';
import { POS_ROUTE_FEATURES } from '@/config/routeFeatures';
import { resolveOrgRoutePermission } from '@/utils/navigation';
import { useAppSelector } from '@/hooks/useAppSelector';
import type { RootState } from '@/store';
import {
  isOnboardingDone,
  isPlatformOnboardingRequired,
  isFattahOneProductSelectionRequired,
  isProductSelectionDone,
} from '@/modules/onboarding/onboardingState';
import { resolveLoginRedirect } from '@/routes/ModuleScopeGuard';
import { useActiveModule } from '@/hooks/useActiveModule';
import { getPostLoginPath } from '@/utils/moduleScope';
import { evaluateOrgRouteDenial, evaluatePosRouteDenial } from '@/utils/routeAccessPolicy';
import { getLauncherModules, isOrgOwner, resolveOwnerHomePath, resolveSingleAppHomePath } from '@/utils/moduleAccess';
import { canAccessWorkspacePath } from '@/utils/moduleNavigation';
import { usePermissions } from '@/hooks/usePermissions';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { getModuleEntryByLoginPath } from '@/config/moduleEntries';
import { resolveModuleAdminRoutePermission } from '@/config/moduleDefaults';
import { ORG_ROUTE_VIEW_PERMISSIONS } from '@/config/routeAccessRegistry';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import type { ErpModule } from '@/api/platform.api';

type RoleGuard = 'master' | 'org' | 'any';

const ORG_ROUTE_PERMISSIONS = ORG_ROUTE_VIEW_PERMISSIONS;

const POS_ROUTE_PERMISSIONS: Record<string, string | string[] | null> = {
  '/pos': 'BILLING_CREATE',
  '/pos/held': 'BILLING_VIEW',
  '/pos/shift': 'BILLING_CREATE',
  '/pos/returns': 'BILLING_RETURN',
  '/pos/analytics': ['BILLING_VIEW', 'REPORT_VIEW'],
  '/pos/setup': ['POS_ADMIN', 'STORE_MANAGE'],
  '/pos/terminal-reports': 'REPORT_VIEW',
  '/pos/shortcuts': 'BILLING_CREATE',
};

interface ProtectedRouteProps {
  role?: RoleGuard;
  permission?: string | string[];
}

type ProtectedRouteOutletProps = ProtectedRouteProps & {
  user: NonNullable<RootState['auth']['user']>;
  entryModule: ErpModule | null | undefined;
};

function ProtectedRouteOutlet({ role = 'any', permission, user, entryModule }: ProtectedRouteOutletProps) {
  const location = useLocation();
  const { hasFeature, loading: featuresLoading } = useFeatures();
  const enabledModules = useAppSelector((s) => s.auth.enabledModules);
  const accessibleModules = useAppSelector((s) => s.auth.accessibleModules);
  const { has } = usePermissions();

  const routePermission =
    permission ??
    (role === 'org'
      ? (resolveModuleAdminRoutePermission(location.pathname) ??
        ORG_ROUTE_PERMISSIONS[location.pathname] ??
        resolveOrgRoutePermission(location.pathname))
      : role === 'any'
        ? POS_ROUTE_PERMISSIONS[location.pathname]
        : undefined);

  const perms = user.permissions ?? [];
  const owner = isOrgOwner(perms, user.roleCode);
  const scopedModule: ErpModule | null =
    !user.masterAdmin && role === 'org'
      ? owner
        ? (resolveModuleFromPath(location.pathname) ?? entryModule ?? null)
        : (entryModule ?? null)
      : null;

  const accessDenial = useMemo(() => {
    if (user.masterAdmin) return null;

    if (role === 'org' && !location.pathname.startsWith('/org/locked')) {
      return evaluateOrgRouteDenial({
        pathname: location.pathname,
        permissions: perms,
        roleCode: user.roleCode,
        masterAdmin: false,
        entryModule,
        scopedModule,
        routePermission,
        hasFeature,
        featuresLoading,
      });
    }

    if (role === 'any') {
      return evaluatePosRouteDenial({
        pathname: location.pathname,
        permissions: perms,
        masterAdmin: false,
        routePermission,
        hasFeature,
        featuresLoading,
        posRouteFeatures: POS_ROUTE_FEATURES,
        entryModule,
      });
    }

    return null;
  }, [
    user.masterAdmin,
    user.roleCode,
    role,
    location.pathname,
    perms,
    entryModule,
    scopedModule,
    routePermission,
    hasFeature,
    featuresLoading,
  ]);

  const launcherCodes = useMemo(
    () =>
      getLauncherModules(enabledModules, perms, accessibleModules, user.roleCode, has).map(
        (entry) => entry.erpModule,
      ),
    [enabledModules, perms, accessibleModules, user.roleCode, has],
  );

  const canAccessPath = useMemo(
    () => (pathname: string) => {
      if (user.masterAdmin || pathname.startsWith('/master')) return true;
      return canAccessWorkspacePath(pathname, enabledModules, launcherCodes, accessibleModules);
    },
    [accessibleModules, enabledModules, launcherCodes, user.masterAdmin],
  );

  const fallbackPath = entryModule ? getPostLoginPath(entryModule, MODULE_HUB_PATH) : MODULE_HUB_PATH;

  return (
    <RouteAccessGate
      denied={accessDenial != null}
      message={accessDenial?.message}
      fallbackPath={accessDenial?.fallbackPath ?? fallbackPath}
      silent={accessDenial?.silent}
      canAccessPath={canAccessPath}
    />
  );
}

export function ProtectedRoute({ role = 'any', permission }: ProtectedRouteProps) {
  const { isAuthenticated, user, initialized, entryModule, enabledModules } = useAppSelector((s) => s.auth);
  const location = useLocation();
  const activeModule = useActiveModule();

  if (!initialized) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    const loginPath = resolveLoginRedirect(location.pathname, entryModule, activeModule);
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  if (role === 'master' && !user.masterAdmin) {
    return <Navigate to="/master/login" replace />;
  }

  if (role === 'org' && user.masterAdmin) {
    return <Navigate to="/master" replace />;
  }

  if (
    role === 'org' &&
    !user.masterAdmin &&
    !entryModule &&
    !isOrgOwner(user.permissions ?? [], user.roleCode)
  ) {
    return <Navigate to={MODULE_HUB_PATH} replace />;
  }

  if (isPlatformOnboardingRequired() && location.pathname === '/onboarding/pos') {
    return <Navigate to="/onboarding" replace />;
  }

  if (
    role === 'org' &&
    !user.masterAdmin &&
    !entryModule &&
    isOrgOwner(user.permissions ?? []) &&
    isPlatformOnboardingRequired()
  ) {
    if (isFattahOneProductSelectionRequired() && location.pathname !== '/onboarding/products') {
      return <Navigate to="/onboarding/products" replace />;
    }
    if (
      isPlatformOnboardingRequired() &&
      isProductSelectionDone() &&
      location.pathname !== '/onboarding' &&
      location.pathname !== '/onboarding/pos'
    ) {
      return <Navigate to="/onboarding" replace />;
    }
  }

  if (isOnboardingDone() && location.pathname.startsWith('/onboarding')) {
    return <Navigate to={resolveOwnerHomePath(enabledModules)} replace />;
  }

  return <ProtectedRouteOutlet role={role} permission={permission} user={user} entryModule={entryModule} />;
}

export function PublicOnlyRoute() {
  const location = useLocation();
  const { isAuthenticated, user, entryModule, workspaceModule, enabledModules, accessibleModules } =
    useAppSelector((s) => s.auth);
  const { has } = usePermissions();
  const permissions = user?.permissions ?? [];
  if (isAuthenticated && user) {
    const moduleLoginEntry = getModuleEntryByLoginPath(location.pathname);
    if (moduleLoginEntry) {
      const activeModule = entryModule ?? workspaceModule;
      if (activeModule === moduleLoginEntry.erpModule) {
        return <Navigate to={moduleLoginEntry.homePath} replace />;
      }
      // Already signed in to another app — allow product login page to switch workspace.
      return <Outlet />;
    }

    if (user.masterAdmin) {
      return <Navigate to="/master" replace />;
    }
    if (entryModule) {
      return <Navigate to={getPostLoginPath(entryModule, resolveOwnerHomePath(enabledModules))} replace />;
    }
    if (isFattahOneProductSelectionRequired()) {
      return <Navigate to="/onboarding/products" replace />;
    }
    if (isPlatformOnboardingRequired()) {
      return <Navigate to="/onboarding" replace />;
    }
    const soleHome = resolveSingleAppHomePath(
      enabledModules,
      has,
      permissions,
      accessibleModules,
      user.roleCode,
    );
    if (soleHome) {
      return <Navigate to={soleHome} replace />;
    }
    if (isOrgOwner(user.permissions ?? [], user.roleCode)) {
      return <Navigate to={resolveOwnerHomePath(enabledModules)} replace />;
    }
    return <Navigate to={MODULE_HUB_PATH} replace />;
  }
  return <Outlet />;
}

